import { useEffect, useRef, useState, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useOpsTracker — encapsulates live GPS tracking for ops portal.
 * Sends location every 30s + on any movement >10m via watchPosition.
 */
export default function useOpsTracker(opsPhone) {
  const [trackingActive, setTrackingActive] = useState(false);
  const [trackingStatus, setTrackingStatus] = useState("en_route");
  const [geoError, setGeoError] = useState("");
  const watchRef = useRef(null);
  const intervalRef = useRef(null);

  const sendLocation = useCallback(async (lat, lng, accuracy) => {
    try {
      await fetch(`${API_URL}/api/tracker/location`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ops_phone: opsPhone, lat, lng, accuracy, status: trackingStatus }),
      });
    } catch (err) { console.error('[Tracker]', err?.message); }
  }, [opsPhone, trackingStatus]);

  const start = useCallback(() => {
    if (!navigator.geolocation) return;
    setTrackingActive(true);
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => sendLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    }, 30000);
  }, [sendLocation]);

  const stop = useCallback(() => {
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    watchRef.current = null;
    intervalRef.current = null;
    setTrackingActive(false);
    fetch(`${API_URL}/api/tracker/stop`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ops_phone: opsPhone }),
    }).catch(err => console.error('[Tracker stop]', err?.message));
  }, [opsPhone]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const getLocation = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation not supported");
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => { setGeoError("Location access denied"); resolve(null); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }), []);

  return {
    trackingActive, trackingStatus, setTrackingStatus,
    start, stop, getLocation,
    geoError, setGeoError,
  };
}
